/**
 * Detección del slug de la web de empleos desde el subdominio:
 * {slug}.amandahr.ai → slug. Devuelve null en app/www/api/etc. (reservados),
 * en el dominio raíz, en localhost y en cualquier otro host.
 *
 * Mantener alineado con la lista de reservados del backend
 * (server/src/utils/slug.utils.js).
 */

const BASE_DOMAIN = 'amandahr.ai';

const RESERVED_SUBDOMAINS = [
  // Subdominios en uso por la plataforma (DNS real)
  'www', 'api', 'app', 'redis', 'send', 'mail', 'smtp', 'ftp',
  // Términos de plataforma / phishing
  'admin', 'administrador', 'portal', 'empleos', 'jobs', 'login',
  'registro', 'signup', 'auth', 'soporte', 'support', 'ayuda', 'help',
  'pagos', 'payments', 'billing', 'amanda', 'amandahr',
  'staging', 'dev', 'test', 'demo',
];

export const getHostBoardSlug = () => {
  const host = window.location.hostname.toLowerCase();
  const suffix = `.${BASE_DOMAIN}`;

  if (!host.endsWith(suffix)) return null;

  const sub = host.slice(0, -suffix.length);
  // Solo un nivel de subdominio, no vacío y no reservado
  if (!sub || sub.includes('.') || RESERVED_SUBDOMAINS.includes(sub)) return null;

  return sub;
};

/**
 * URL de la web de empleos de una organización. En producción cada org vive en
 * su subdominio ({slug}.amandahr.ai); fuera del dominio productivo (localhost,
 * previews) se usa la ruta /empleos/:slug para que el link siga funcionando.
 */
export const getBoardUrl = (slug) => {
  if (!slug) return '/empleos';
  const host = window.location.hostname.toLowerCase();
  if (host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) {
    return `https://${slug}.${BASE_DOMAIN}`;
  }
  return `/empleos/${slug}`;
};

/**
 * Link público de una feria (PLAN_FERIAS). En producción va en el subdominio de
 * la organización si su web de empleos está publicada (boardSlug); si no, en
 * app.amandahr.ai. La página se busca por el slug de la feria (fijo desde que
 * se crea), no por el subdominio: si la organización cambia su slug, los QR ya
 * impresos siguen funcionando. Fuera del dominio productivo (localhost,
 * previews) usa el origen actual para que el link se pueda probar.
 */
export const getFairUrl = (fairSlug, boardSlug = null) => {
  if (!fairSlug) return '';
  const path = `/feria/${fairSlug}`;
  const host = window.location.hostname.toLowerCase();
  if (host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) {
    return boardSlug ? `https://${boardSlug}.${BASE_DOMAIN}${path}` : `https://app.${BASE_DOMAIN}${path}`;
  }
  return `${window.location.origin}${path}`;
};

/**
 * Link público de una vacante (/vacante/:id, 2026-10-25): el que entrega el
 * ATS y el que leen las redes al armar la previsualización. En producción va
 * en el subdominio de la organización si tiene slug, aunque su web de empleos
 * no esté publicada (como los links de tests y documentos); sin slug, en
 * app.amandahr.ai. La página se busca por el id de la vacante, no por el
 * subdominio. Fuera del dominio productivo (localhost, previews) usa el origen
 * actual para que el link se pueda probar.
 */
export const getPositionUrl = (positionId, slug = null) => {
  if (!positionId) return '';
  const path = `/vacante/${encodeURIComponent(positionId)}`;
  const host = window.location.hostname.toLowerCase();
  if (host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) {
    return slug ? `https://${slug}.${BASE_DOMAIN}${path}` : `https://app.${BASE_DOMAIN}${path}`;
  }
  return `${window.location.origin}${path}`;
};
